import { cn } from '@myosh/odin-components';
import type { Node, NodeProps } from '@xyflow/react';
import type React from 'react';
import { useMemo } from 'react';
import { makeDataCy } from '../../../helpers/util';
import { useAppSelector } from '../../../redux/hooks';
import { selectMoving } from '../../../redux/slices/diagram';
import BaseNode, { type BaseNodeData } from './base-node.component';

type EscalationFactorBarrierNodeData = BaseNodeData;
export type EscalationFactorBarrierNodeType = Node<EscalationFactorBarrierNodeData>;
type EscalationFactorBarrierNodeProps = NodeProps<EscalationFactorBarrierNodeType>;

// Control-like styling for barrier (CAA convention - neutral, same as normal controls)
const className = 'bg-efb-bg border-efb-border text-efb-fg border-dashed';
const iconClassName = 'text-efb-bg stroke-efb-fg';

/**
 * A component that represents an escalation factor barrier (erosion control) node in a bowtie diagram.
 * These barriers connect to escalation factors to control or mitigate them.
 * Styled to match control nodes per CAA convention.
 *
 * @component
 * @param {EscalationFactorBarrierNodeProps} props - The properties for the escalation factor barrier node component
 * @returns {JSX.Element} A rendered escalation factor barrier node component
 *
 * @example
 * ```tsx
 * <EscalationFactorBarrierNode
 *   id="escalation-barrier-1"
 *   data={{ label: "Erosion Control" }}
 *   position={{ x: 100, y: 100 }}
 * />
 * ```
 */
const EscalationFactorBarrierNode = (props: EscalationFactorBarrierNodeProps): React.JSX.Element => {
  const moving = useAppSelector(selectMoving);

  const data = useMemo(() => {
    const modifiedClassName = cn(className, { 'animate-wobble': moving });
    return {
      ...props.data, // Preserve handles and other properties
      className: modifiedClassName,
      iconClassName,
      showRecordIndicators: true,
      showUnlinkActionIcon: true,
    };
  }, [props.data, moving]);

  const dataCyLabel = useMemo(() => makeDataCy(props.data.label ?? ''), [props.data.label]);

  return (
    <div data-cy={`parent-escalation-factor-barrier-node-container-${dataCyLabel}`} className="flex items-center">
      <BaseNode {...props} data={data} />
    </div>
  );
};

export default EscalationFactorBarrierNode;
export const id = 'escalation-factor-barrier-node';
